import { Injectable, Inject, Optional } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { UIRouter } from '@uirouter/angular';
import { MonLoadingService } from '@monsido/services/loading/services/loading.service';
import { IS_MICROFRONTEND, MFE_BASE_URL_TOKEN, SHADOW_ROOT_TOKEN } from '@client/mfe/tokens/mfe-tokens';


export type Theme = 'legacy' | 'modern';

@Injectable({
    providedIn: 'root',
})
export class ThemeService {
    private legacyStylesheet: HTMLLinkElement | null = null;
    private modernStylesheet: HTMLLinkElement | null = null;
    private currentTheme?: 'legacy' | 'modern';
    private stylesheetLoadingPromise: Promise<void> | null = null;
    private loader: { close(): void } | null = null;

    constructor (
        @Inject(DOCUMENT) readonly document: Document,
        readonly uiRouter: UIRouter,
        readonly loadingService: MonLoadingService,
        @Optional() @Inject(IS_MICROFRONTEND) private readonly isMicrofrontend: boolean | null,
        @Optional() @Inject(SHADOW_ROOT_TOKEN) private readonly shadowRoot: ShadowRoot | null,
        @Optional() @Inject(MFE_BASE_URL_TOKEN) private readonly mfeBaseUrl: string | null,
    ) {
        this.initializeThemeFromRoute();
        this.listenToUIRouterTransitions();
    }

    init (): void {
        if (this.isMicrofrontend) {
            if (this.getCurrentTheme() === 'legacy') {
                void this.loadLegacyTheme();
            } else {
                void this.loadModernTheme();
            }
        } else {
            this.loadModernTheme().then();
            void this.createStylesheet('/shared-global.css');
            void this.createStylesheet('/topmenu-legacy-styles.css');
        }
    }

    public getStylesheetLoadingPromise (): Promise<void> | null {
        return this.stylesheetLoadingPromise;
    }

    public getCurrentTheme (): Theme {
        return this.currentTheme ?? 'legacy';
    }

    private setTheme (theme: Theme): void {
        if (this.isMicrofrontend) {
            this.applyThemeMarkerOnMFE(theme);
        } else {
            this.applyThemeMarker(theme);
        }

        if (this.currentTheme === theme) {
            return;
        }

        const previousTheme = this.currentTheme;
        this.currentTheme = theme;
        this.updateStylesheets(previousTheme !== undefined);
    }

    private listenToUIRouterTransitions (): void {
        this.uiRouter.transitionService.onBefore({}, async (transition) => {
            // Detect theme from state data or state name.
            const stateData = transition.$to().data;
            if (stateData?.theme) {
                this.setTheme(stateData.theme);
            } else {
                const stateName = transition.to().name || '';
                this.setTheme(this.getThemeFromUrl(stateName));
            }

            // Wait for stylesheet before allowing the transition to proceed.
            if (this.stylesheetLoadingPromise) {
                await this.stylesheetLoadingPromise;
            }
            return true;
        });

        this.uiRouter.transitionService.onFinish({}, () => {
            if (this.loader) {
                this.loader.close();
            }
            this.loader = null;
            return true;
        });

        this.uiRouter.transitionService.onError({}, () => {
            if (this.loader) {
                this.loader.close();
            }
            this.loader = null;
        });
    }

    private initializeThemeFromRoute (): void {
        const currentUrl = window.location.pathname;
        const theme = this.getThemeFromUrl(currentUrl);
        this.setTheme(theme);
    }

    private getThemeFromUrl (url: string): 'legacy' | 'modern' {
        if ((url.includes('admin') && !url.includes('admin_old')) ||
            url.includes('accessibility') ||
            url.includes('notifications') ||
            url.includes('edit-profile')) {
            return 'modern';
        }
        return 'legacy';
    }

    private applyThemeMarkerOnMFE (theme: Theme): void {
        const rootEl = this.shadowRoot?.querySelector('mfe-root');
        if (!rootEl) {
            return;
        }

        rootEl.setAttribute('data-ui-theme', theme);
        rootEl.setAttribute('data-ui-shell', theme);
    }

    private applyThemeMarker (theme: Theme): void {
        this.document.documentElement.setAttribute('data-ui-theme', theme);
        this.document.body.setAttribute('data-ui-theme', theme);
        this.document.body.setAttribute('data-ui-shell', theme);
    }

    private updateStylesheets (showLoader: boolean = false): void {
        this.loader = showLoader ? this.loadingService.show() : null;

        this.stylesheetLoadingPromise = this.withTimeout(Promise.resolve().then(async () => {
            if (this.currentTheme === 'modern') {
                await this.loadModernTheme();
                this.removeStylesheet(this.legacyStylesheet);
            } else {
                await this.loadLegacyTheme();
                this.removeStylesheet(this.modernStylesheet);
                this.modernStylesheet = null;
            }

            try {
                await this.withTimeout(this.document.fonts.ready, 10_000);
            } catch {}
            this.stylesheetLoadingPromise = null;
        }).catch(() => {
            this.stylesheetLoadingPromise = null;
            if (this.loader) {
                this.loader.close();
            }
        }), 15_000);
    }

    private createStylesheet (href: string): Promise<HTMLLinkElement> {
        return new Promise((resolve, reject) => {
            const link = this.document.createElement('link');
            link.rel = 'stylesheet';
            // In MFE mode, use the MFE's base URL to load stylesheets from the correct origin
            link.href = this.isMicrofrontend && this.mfeBaseUrl
                ? `${this.mfeBaseUrl.replace(/\/$/, '')}/${href.replace(/^\//, '')}`
                : href;
            link.onload = () => resolve(link);
            link.onerror = () => reject(new Error(`Failed to load stylesheet: ${href}`));
            if (this.isMicrofrontend && this.shadowRoot) {
                this.shadowRoot.appendChild(link);
                return;
            }
            this.document.head.appendChild(link);
        });
    }

    private removeStylesheet (stylesheet: HTMLLinkElement | null): void {
        if (stylesheet?.parentNode) {
            stylesheet.parentNode.removeChild(stylesheet);
        }
    }

    private async loadLegacyTheme (): Promise<void> {
        this.legacyStylesheet = await this.createStylesheet('/legacy-theme.css');
    }

    private withTimeout<T> (promise: Promise<T>, duration: number): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            const timer = setTimeout(() => reject(new Error(`Timed out after ${duration}ms`)), duration);
            promise.then(
                (value) => {
                    clearTimeout(timer); resolve(value);
                },
                (error) => {
                    clearTimeout(timer); resolve(error);
                },
            );
        });
    }

    private async loadModernTheme (): Promise<void> {
        this.document.documentElement.style.fontSize = '16px';
        this.modernStylesheet = await this.createStylesheet('/modern-theme.css');
    }
}
